import React, {useCallback, useEffect, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {ChevronLeft, ChevronRight, ImageIcon, X} from 'lucide-react';
import {useSeo} from '../hooks/useSeo';
import {galleryImages} from '../assets/gallery';

// Zdjęcia realizacji są zoptymalizowane do .webp i wygenerowane automatycznie
// przez scripts/convert-gallery.mjs (barrel: src/assets/gallery/index.ts).
// Aby dodać kolejne: wrzuć pliki do src/assets/gallery i uruchom
// `node scripts/convert-gallery.mjs`.
interface Photo {
    src: string;
    alt: string;
}

const photos: Photo[] = galleryImages.map((src, i) => ({
    src,
    alt: `Realizacja montażu kamer i monitoringu w Mielcu i okolicach - zdjęcie ${i + 1}`,
}));

// Liczba pustych kafelków pokazywanych, dopóki nie dodasz zdjęć.
const PLACEHOLDER_COUNT = 9;

export function WykonaniaPage() {
    useSeo({
        title: 'Nasze realizacje - montaż kamer i monitoringu w Mielcu i okolicach | kamerymielec.pl',
        description:
            'Galeria realizacji: montaż kamer, monitoringu CCTV, alarmów i sieci IT w Mielcu oraz w promieniu do 250 km (Rzeszów, Tarnów, Dębica, Stalowa Wola, Tarnobrzeg, Sandomierz). Zobacz nasze prace.',
        canonicalPath: '/wykonania',
        keywords:
            'realizacje kamery Mielec, portfolio monitoring Mielec, montaż CCTV przykłady, galeria kamer Mielec, montaż kamer zdjęcia',
        breadcrumbs: [
            {name: 'Strona główna', path: '/'},
            {name: 'Realizacje', path: '/wykonania'},
        ],
    });

    const hasPhotos = photos.length > 0;
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

    const openLightbox = useCallback((i: number) => setLightboxIndex(i), []);
    const closeLightbox = useCallback(() => setLightboxIndex(null), []);
    const showPrev = useCallback(
        () => setLightboxIndex((i) => (i === null ? i : (i - 1 + photos.length) % photos.length)),
        [],
    );
    const showNext = useCallback(
        () => setLightboxIndex((i) => (i === null ? i : (i + 1) % photos.length)),
        [],
    );

    // Klawiatura: Esc zamyka, strzałki nawigują między zdjęciami.
    useEffect(() => {
        if (lightboxIndex === null) return;

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeLightbox();
            else if (e.key === 'ArrowLeft') showPrev();
            else if (e.key === 'ArrowRight') showNext();
        };

        window.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';

        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [lightboxIndex, closeLightbox, showPrev, showNext]);

    return (
        <section className="w-full py-20 md:py-28 px-5">
            <div className="max-w-6xl mx-auto">
                <motion.div
                    initial={{opacity: 0, y: 30}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.6}}
                    className="text-center mb-12"
                >
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                        Nasze <span className="gradient-text">wykonania</span>
                    </h1>
                    <p className="text-muted-foreground max-w-xl mx-auto">
                        Galeria realizacji montażu kamer i monitoringu w Mielcu oraz w promieniu do 250 km.
                    </p>
                </motion.div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                    {hasPhotos
                        ? photos.map((photo, i) => (
                            <motion.button
                                key={i}
                                type="button"
                                onClick={() => openLightbox(i)}
                                initial={{opacity: 0, scale: 0.96}}
                                animate={{opacity: 1, scale: 1}}
                                transition={{duration: 0.4, delay: i * 0.05}}
                                aria-label={`Powiększ zdjęcie ${i + 1}`}
                                className="group aspect-square rounded-2xl overflow-hidden border border-border/50 bg-card cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            >
                                <img
                                    src={photo.src}
                                    alt={photo.alt}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                            </motion.button>
                        ))
                        : Array.from({length: PLACEHOLDER_COUNT}).map((_, i) => (
                            <motion.div
                                key={i}
                                initial={{opacity: 0, scale: 0.96}}
                                animate={{opacity: 1, scale: 1}}
                                transition={{duration: 0.4, delay: i * 0.05}}
                                className="aspect-square rounded-2xl border border-dashed border-border bg-muted/40 flex items-center justify-center"
                            >
                                <ImageIcon size={32} className="text-muted-foreground/40"/>
                            </motion.div>
                        ))}
                </div>
            </div>

            <AnimatePresence>
                {lightboxIndex !== null && (
                    <Lightbox
                        photo={photos[lightboxIndex]}
                        index={lightboxIndex}
                        total={photos.length}
                        onClose={closeLightbox}
                        onPrev={showPrev}
                        onNext={showNext}
                    />
                )}
            </AnimatePresence>
        </section>
    );
}

function Lightbox({
                      photo,
                      index,
                      total,
                      onClose,
                      onPrev,
                      onNext,
                  }: {
    photo: Photo;
    index: number;
    total: number;
    onClose: () => void;
    onPrev: () => void;
    onNext: () => void;
}) {
    return (
        <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.2}}
            onClick={onClose}
            className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 sm:p-8"
            role="dialog"
            aria-modal="true"
            aria-label={`Zdjęcie ${index + 1} z ${total}`}
        >
            <button
                type="button"
                onClick={onClose}
                aria-label="Zamknij"
                className="absolute top-4 right-4 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
                <X size={22}/>
            </button>

            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onPrev();
                }}
                aria-label="Poprzednie zdjęcie"
                className="absolute left-3 sm:left-6 z-10 flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
                <ChevronLeft size={26}/>
            </button>

            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onNext();
                }}
                aria-label="Następne zdjęcie"
                className="absolute right-3 sm:right-6 z-10 flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
                <ChevronRight size={26}/>
            </button>

            <motion.img
                key={photo.src}
                src={photo.src}
                alt={photo.alt}
                onClick={(e) => e.stopPropagation()}
                initial={{opacity: 0, scale: 0.97}}
                animate={{opacity: 1, scale: 1}}
                transition={{duration: 0.2}}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl select-none"
            />

            <div
                className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-white text-sm font-medium">
                {index + 1} / {total}
            </div>
        </motion.div>
    );
}
